/**
 * SEO Utility — manages document head tags for per-page SEO.
 *
 * In a client-side SPA every page shares one <head>. This helper
 * creates / updates the relevant <meta>, <link>, and <script> tags
 * so each route gets its own title, description, OG cards, canonical,
 * and JSON-LD structured data.
 *
 * Usage:
 *   useEffect(() => { updateSEO({ title: "…", description: "…", … }); }, []);
 *
 * @module seo
 */

/* ── Constants ────────────────────────────────────────────────────────────── */

const SITE_NAME    = "SaferSavvy";
const SITE_URL     = "https://www.safersavvy.tech";
const DEFAULT_OG   = `${SITE_URL}/favicon.svg`;    // fallback OG image
const TWITTER_HANDLE = "@safersavvy";

/* ── Types ────────────────────────────────────────────────────────────────── */

export interface SEOConfig {
  title: string;
  description: string;
  /** Canonical path (e.g. "/why-safersavvy"). Defaults to current path. */
  canonical?: string;
  /** Full URL to an Open Graph image. Falls back to DEFAULT_OG. */
  ogImage?: string;
  /** OG type. Defaults to "website". */
  ogType?: string;
  /** Comma-separated keyword list. */
  keywords?: string;
  /** Prevents indexing if true (for dashboards etc.) */
  noIndex?: boolean;
  /** JSON-LD structured data objects to inject. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setMetaProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.content = content;
}

function setCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(data: Record<string, unknown> | Record<string, unknown>[]) {
  // Remove previous SEO JSON-LD blocks
  document.querySelectorAll('script[data-seo-jsonld]').forEach((el) => el.remove());

  const items = Array.isArray(data) ? data : [data];
  items.forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo-jsonld", "true");
    script.textContent = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

/* ── Main export ──────────────────────────────────────────────────────────── */

export function updateSEO(config: SEOConfig) {
  const {
    title,
    description,
    canonical,
    ogImage   = DEFAULT_OG,
    ogType    = "website",
    keywords,
    noIndex   = false,
    jsonLd,
  } = config;

  // Title
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  document.title = fullTitle;

  // Description
  setMeta("description", description);

  // Keywords (if provided)
  if (keywords) setMeta("keywords", keywords);

  // Robots
  setMeta("robots", noIndex ? "noindex, nofollow" : "index, follow, max-image-preview:large, max-snippet:-1");

  // Canonical
  const canonicalPath = canonical ?? window.location.pathname;
  setCanonical(`${SITE_URL}${canonicalPath}`);

  // Open Graph
  setMetaProperty("og:title",       fullTitle);
  setMetaProperty("og:description", description);
  setMetaProperty("og:type",        ogType);
  setMetaProperty("og:url",         `${SITE_URL}${canonicalPath}`);
  setMetaProperty("og:image",       ogImage);
  setMetaProperty("og:site_name",   SITE_NAME);
  setMetaProperty("og:locale",      "en_US");

  // Twitter Cards
  setMeta("twitter:card",        "summary_large_image");
  setMeta("twitter:site",        TWITTER_HANDLE);
  setMeta("twitter:title",       fullTitle);
  setMeta("twitter:description", description);
  setMeta("twitter:image",       ogImage);

  // JSON-LD
  if (jsonLd) injectJsonLd(jsonLd);
}

/* ── Pre-built JSON-LD schemas ────────────────────────────────────────────── */

export const ORGANIZATION_SCHEMA: Record<string, unknown> = {
  "@context":      "https://schema.org",
  "@type":         "Organization",
  name:            "SaferSavvy",
  url:             SITE_URL,
  logo:            `${SITE_URL}/favicon.svg`,
  description:     "AI-powered university transport automation platform with real-time tracking, smart routing, and automated attendance.",
  foundingDate:    "2024",
  contactPoint: {
    "@type":       "ContactPoint",
    email:         "info@safersavvy.tech",
    telephone:     "+92-327-4025364",
    contactType:   "customer service",
    areaServed:    "PK",
    availableLanguage: ["English", "Urdu"],
  },
  address: {
    "@type":         "PostalAddress",
    addressLocality: "Lahore",
    addressRegion:   "Punjab",
    addressCountry:  "PK",
    streetAddress:   "University of Engineering and Technology",
  },
  sameAs: [
    "https://twitter.com/safersavvy",
    "https://facebook.com/safersavvy",
    "https://instagram.com/safersavvy",
    "https://linkedin.com/company/safersavvy",
  ],
};

export const WEBSITE_SCHEMA: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@type":    "WebSite",
  name:       "SaferSavvy",
  url:        SITE_URL,
  potentialAction: {
    "@type":  "SearchAction",
    target:   `${SITE_URL}/search?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

export const SOFTWARE_SCHEMA: Record<string, unknown> = {
  "@context":          "https://schema.org",
  "@type":             "SoftwareApplication",
  name:                "SaferSavvy",
  applicationCategory: "EducationApplication",
  operatingSystem:     "Web, Android, iOS",
  description:         "AI-powered university transport automation platform with real-time tracking, smart routing, and automated attendance.",
  offers: {
    "@type": "Offer",
    price:   "0",
    priceCurrency: "USD",
  },
  aggregateRating: {
    "@type":      "AggregateRating",
    ratingValue:  "4.8",
    ratingCount:  "12400",
    bestRating:   "5",
    worstRating:  "1",
  },
};

export function buildBreadcrumbSchema(items: { name: string; url: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type":    "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type":  "ListItem",
      position: i + 1,
      name:     item.name,
      item:     `${SITE_URL}${item.url}`,
    })),
  };
}

export function buildFAQSchema(faqs: { question: string; answer: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type":    "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name:    faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text:    faq.answer,
      },
    })),
  };
}
